import { useEffect, useState, useMemo } from "react";
import { useProject } from "../../../../hooks";
import { useCart, useLabor } from "../../../../hooks/query";
import useLaborFormulaEvaluator from "../../hooks/useLaborFormulaEvaluator";

export default function useLaborInit(measurements?: any[]) {
    const { project } = useProject();
    const { useListByProjectId } = useCart();
    const { useListLaborServices, useListLaborInventory } = useLabor();
    const { evaluateFormula } = useLaborFormulaEvaluator();

    const cartListSvc = useListByProjectId();
    const { data: allLaborServices, isLoading: isServicesLoading } = useListLaborServices();
    const { data: supplierInventory, isLoading: isInventoryLoading } = useListLaborInventory();

    const [laborSchema, setLaborSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getCartItems = async () => {
        if (!project?.id) return [];
        try {
            const cartItems = await cartListSvc.mutateAsync({ ProjectId: project.id });
            const laborCartItems = cartItems.filter((item: any) => item.LaborServiceId);
            setCart(laborCartItems);
            return laborCartItems;
        } catch (error) {
            console.error("Error fetching cart items:", error);
            return [];
        }
    };

    // Auto-identify required labor based on formula results > 0
    useEffect(() => {
        if (isServicesLoading || !allLaborServices || !measurements) return;

        const results = evaluateFormula(measurements, allLaborServices, {});

        const identified = allLaborServices.filter((service: any) => {
            const quantity = results[service.Id];
            return quantity !== null && quantity !== undefined && quantity > 0;
        });

        setLaborSchema(identified);
    }, [allLaborServices, measurements, isServicesLoading, evaluateFormula]);

    // Calculate price ranges from supplier inventory
    useEffect(() => {
        if (!supplierInventory || !laborSchema.length) return;

        const ranges: Record<string, { min: number; max: number }> = {};

        laborSchema.forEach((service: any) => {
            const prices = supplierInventory
                .filter((inv: any) => inv.LaborService_Id === service.Id && inv.UnitPrice > 0)
                .map((inv: any) => Number(inv.UnitPrice));

            if (prices.length > 0) {
                ranges[service.Id] = {
                    min: Math.min(...prices),
                    max: Math.max(...prices),
                };
            }
        });

        setPriceRanges(ranges);
    }, [supplierInventory, laborSchema]);

    useEffect(() => {
        const loadCart = async () => {
            if (project?.id) {
                setIsLoading(true);
                await getCartItems();
                setIsLoading(false);
            }
        };
        loadCart();
    }, [project?.id]);

    const finalLoading = isServicesLoading || isInventoryLoading || isLoading;

    return {
        allLaborItems: allLaborServices || [],
        laborItems: laborSchema,
        laborSchema,
        cart,
        priceRanges,
        isLoading: finalLoading,
    };
}
